import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend } from 'recharts';

interface GaugeChartProps {
  value: number;
  min: number;
  max: number;
  title: string;
  unit: string;
  thresholds?: Array<{
    value: number;
    color: string;
  }>;
}

export function GaugeChart({ value, min, max, title, unit, thresholds = [] }: GaugeChartProps) {
  // Ensure value is within min/max range
  const safeValue = Math.max(min, Math.min(max, value));

  // Calculate percentage for the needle
  const percent = ((safeValue - min) / (max - min)) * 100;

  // Define the gauge segments
  const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value);

  // Create gauge segments based on thresholds
  const segments = [];
  let prevValue = min;

  // Add segments based on thresholds
  for (const threshold of sortedThresholds) {
    if (threshold.value > prevValue && threshold.value <= max) {
      segments.push({
        name: `${prevValue}-${threshold.value}`,
        value: threshold.value - prevValue,
        color: threshold.color,
      });
      prevValue = threshold.value;
    }
  }

  // Add final segment if needed
  if (prevValue < max) {
    segments.push({
      name: `${prevValue}-${max}`,
      value: max - prevValue,
      color: sortedThresholds.length > 0 ? sortedThresholds[sortedThresholds.length - 1].color : '#8884d8',
    });
  }

  // If no segments created (no valid thresholds), create a default segment
  if (segments.length === 0) {
    segments.push({
      name: `${min}-${max}`,
      value: max - min,
      color: '#8884d8',
    });
  }

  // Data for the needle
  const needleValue = percent;
  const needleData = [
    { name: 'needle', value: needleValue },
    { name: 'needleBase', value: 100 - needleValue },
  ];

  // Get color based on current value
  const getCurrentColor = () => {
    if (sortedThresholds.length === 0) return '#8884d8';

    // Find the appropriate color based on value thresholds
    for (let i = 0; i < sortedThresholds.length; i++) {
      if (safeValue <= sortedThresholds[i].value) {
        return sortedThresholds[i].color;
      }
    }

    // If value exceeds all thresholds, use the highest threshold color
    return sortedThresholds[sortedThresholds.length - 1].color;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col items-center">
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={segments}
                  cx="50%"
                  cy="80%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={1}
                  dataKey="value"
                >
                  {segments.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Pie
                  data={needleData}
                  cx="50%"
                  cy="80%"
                  startAngle={180}
                  endAngle={0}
                  innerRadius={0}
                  outerRadius={20}
                  dataKey="value"
                >
                  <Cell fill="transparent" />
                  <Cell fill="transparent" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-2 text-center">
            <div className="text-3xl font-bold" style={{ color: getCurrentColor() }}>
              {safeValue.toFixed(1)} <span className="text-base">{unit}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-1 flex justify-between w-full px-4">
              <span>{min}</span>
              <span>{max}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
